

























































































































































/* tslint:disable:only-arrow-functions*/

import {Component, Vue} from 'vue-property-decorator';
import AnimatedInput from '@/components/AnimatedInput.vue';
import LoadingButton from '@/components/LoadingButton.vue';
import {Action, Getter, Mutation} from "vuex-class";
import getFileSizeHumanReadable from '@/util/file-size';
import wait from "@/util/wait";
import KeyUtility from "../util/keyUtility";
import Letterbox from "../models/Letterbox";
import LetterboxAdapter from "../adapters/letterbox";
import * as Sentry from '@sentry/browser';
import featureFlags from "@/util/featureFlags";

@Component({
    components: {AnimatedInput, LoadingButton},
})
export default class LetterboxUpload extends Vue {

    @Mutation setFileDragActive!: () => void;
    @Mutation setFileDragInactive!: () => void;
    @Mutation startOverWithUpload!: () => void;
    @Mutation setUploadFile!: (file: File) => void;
    @Mutation addUploadFile!: (file: File) => void;
    @Mutation removeUploadFile!: (file: File) => void;
    @Mutation setMetaKey!: (metaKey: CryptoKey) => void;
    @Mutation setLetterboxAddress!: (address: string) => void;
    @Mutation failUpload!: (errorMessage: string) => Promise<any>;

    @Getter showUploadForm!: boolean;
    @Getter showUploadProgress!: boolean;
    @Getter showUploadSuccess!: boolean;
    @Getter showUploadError!: boolean;
    @Getter hasUploadFile!: boolean;

    @Action uploadMetadata!: (letterboxAddress: string | null) => Promise<any>;
    @Action uploadChunks!: () => Promise<any>;
    @Action commitUpload!: (neededForDecryption: string) => Promise<any>;

    adapter = new LetterboxAdapter();
    letterboxInfo: Letterbox | null = null;
    errorMessage: string | null = null;
    urlMaterial: string = "";
    uploadKey: string = "";
    eventcount: number = 0;
    animateProgress: boolean = true;
    agreesToS: boolean = false;

    get uploadError() {
        return this.$store.state.upload.error;
    }

    get progress() {
        return this.$store.state.upload.progress;
    }

    get fileLink() {
        return this.$store.state.upload.linkForPartner;
    }

    get showAds() {
      return !featureFlags.usersEnabled;
    }

    dragAndDropActive() {
        return this.$store.state.showFileDragActive;
    }

    get files(): File[] {
      return this.$store.state.upload.files as File[];
    }

    sizeHumanReadable(file: File): string {
      return getFileSizeHumanReadable(file.size);
    }

    created() {
        return this.downloadInfo();
    }

    async downloadInfo() {
        try {
            const uploadKey = this.$route.params.id;
            let keyMaterial = this.$route.hash;

            if (keyMaterial === '') {
                this.$swal('Ein Fehler trat auf', 'Der Link war nicht korrekt.', 'error');
            }

            if (keyMaterial.startsWith('#')) {
                keyMaterial = keyMaterial.substring(1); //remove leading #
            }
            this.urlMaterial = keyMaterial;
            this.uploadKey = uploadKey;

            const encryptedInfo = await this.adapter.getLetterboxUploadInfo(uploadKey);

            const b64Key: string = decodeURIComponent(this.urlMaterial);
            const key: CryptoKey = await KeyUtility.loadKeyFromBase64(b64Key);

            this.letterboxInfo = await encryptedInfo.getDecrypted(key);
        } catch (e) {
            Vue.$log.error("Letterbox info could not be downloaded, exception was thrown", e);
            await this.failUpload("Die Briefkasten-Informationen konnten nicht abgerufen werden.");
            Sentry.captureException(e);
            return;
        }
    }

    fileInfo() {
        if (!this.hasUploadFile) {
            return {name: 'Nicht ausgewählt', size: 'Nicht verfügbar'};
        } else {
            const file: File = this.$store.state.upload.file;
            const fileSizeHumanReadable = getFileSizeHumanReadable(file.size);
            return {
                name: file.name,
                size: fileSizeHumanReadable,
            };
        }
    }

    chooseFile() {
      const ref: any = this.$refs.uploadFiles as HTMLInputElement;
      const files = ref.files;

      for (const file of files) {
        this.checkForDuplicatesAndAddFile(file);
      }
    }

    checkForDuplicatesAndAddFile(file: File) {
      const idx: number = (this.$store.state.upload.files as File[]).findIndex((f: File) => {
        return f.name === file.name;
      });

      if (idx > -1) {
        this.$swal('Gleiche Dateinamen', 'Du kannst keine zwei Dateien versenden, die den gleichen Namen tragen.', 'error');
      } else {
        this.addUploadFile(file);
      }
    }

    async uploadFile() {
        const b64Key = decodeURIComponent(this.urlMaterial);
        const metaKey: CryptoKey = await KeyUtility.loadKeyFromBase64(b64Key);
        this.setMetaKey(metaKey);
        await this.uploadMetadata(this.uploadKey);
        this.$log.debug("Metadata uploaded, next: Chunks");
        await this.uploadChunks();
        await wait(550); //Wait for the animation to complete
        this.$log.debug("Chunks uploaded, next: Commit");
        await this.commitUpload(b64Key);
        this.$log.debug("Upload done");
        return;
    }

    async startOver() {
        await this.startOverWithUpload();
        await this.downloadInfo();
    }

  //Drag & Drop setup:
  dragEnterHandler(e: DragEvent) {
    e.preventDefault();
    this.eventcount++;
    this.setFileDragActive();
    return false;
  }

  dragLeaveHandler(e: DragEvent) {
    e.preventDefault();
    // Check if Dragleave is really a leave or just a leave for one container:
    this.eventcount--;
    if (this.eventcount === 0) {
      this.setFileDragInactive();
    }
    return false;
  }

  dragOverHandler(e: DragEvent) {
    e.preventDefault();
  }

  dropHandler(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    //Clear state of upload form:
    this.eventcount = 0;
    this.setFileDragInactive();
    if (!e.dataTransfer) {
      return false;
    }
    if (e.dataTransfer.files) {
      for (const file of e.dataTransfer.files) {
        this.checkForDuplicatesAndAddFile(file);
      }
    } else { //Weird, it should contain files, but lets search manually!
      for (const item of e.dataTransfer.items) {
        if (item.kind === "file") {
          const f: File | null = item.getAsFile();
          if (f) {
            this.checkForDuplicatesAndAddFile(f);
          }
        }
      }
    }
    return false;
  }

  mounted() {
    window.addEventListener('dragenter', this.dragEnterHandler, false);
    window.addEventListener('dragleave', this.dragLeaveHandler, false);
    window.addEventListener('dragover', this.dragOverHandler, false);
    window.addEventListener('drop', this.dropHandler, false);
  }

  beforeDestroy() {
    window.removeEventListener('dragenter', this.dragEnterHandler, false);
    window.removeEventListener('dragleave', this.dragLeaveHandler, false);
    window.removeEventListener('dragover', this.dragOverHandler, false);
    window.removeEventListener('drop', this.dropHandler, false);
  }
}
